import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  MessageCircle, Flag, RefreshCw, Loader2, Check, ChevronLeft, ChevronRight,
  ExternalLink, Search, Filter, X
} from 'lucide-react';
import { toast } from 'sonner';

const PLATFORM_COLORS = {
  facebook: { bg: '#1877F2', label: 'Facebook' },
  instagram: { bg: '#E4405F', label: 'Instagram' },
  linkedin: { bg: '#0A66C2', label: 'LinkedIn' },
  tiktok: { bg: '#000000', label: 'TikTok' },
  youtube: { bg: '#FF0000', label: 'YouTube' },
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function EngagementPage() {
  const [inbox, setInbox] = useState({ comments: [], total: 0, pages: 0, page: 1 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(1);

  // Filters
  const [clientFilter, setClientFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [flaggedFilter, setFlaggedFilter] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Flag dialog
  const [flagDialog, setFlagDialog] = useState(null);
  const [flagNote, setFlagNote] = useState('');

  const fetchInbox = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 30 });
      if (clientFilter !== 'all') params.set('client_name', clientFilter);
      if (platformFilter !== 'all') params.set('platform', platformFilter);
      if (flaggedFilter) params.set('flagged', 'true');
      const data = await api(`/api/engagement/inbox?${params}`);
      setInbox(data);
    } catch (err) {
      toast.error('Failed to load inbox');
    } finally {
      setLoading(false);
    }
  }, [page, clientFilter, platformFilter, flaggedFilter]);

  const fetchStats = async () => {
    try {
      const data = await api('/api/engagement/stats');
      setStats(data);
    } catch (err) {}
  };

  useEffect(() => { fetchInbox(); }, [fetchInbox]);
  useEffect(() => { fetchStats(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    toast.info('Sprout is cancelled. New engagement sync awaits Zernio.');
    try {
      const r = await api('/api/engagement/sync', { method: 'POST' });
      toast.success(`Synced ${r.count} comments`);
      fetchInbox();
      fetchStats();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleFlag = async (commentId, flagged) => {
    try {
      await api(`/api/engagement/${commentId}/flag`, {
        method: 'PUT',
        body: JSON.stringify({ flagged, note: flagNote })
      });
      toast.success(flagged ? 'Comment flagged' : 'Flag removed');
      setFlagDialog(null);
      setFlagNote('');
      fetchInbox();
      fetchStats();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleMarkReplied = async (commentId) => {
    try {
      await api(`/api/engagement/${commentId}/replied`, {
        method: 'PUT',
        body: JSON.stringify({ replied: true })
      });
      toast.success('Marked as replied');
      fetchInbox();
      fetchStats();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const clients = stats ? Object.keys(stats.by_client || {}).sort() : [];
  const platforms = stats ? Object.keys(stats.by_platform || {}).sort() : [];

  const filtered = searchText
    ? inbox.comments.filter(c =>
        c.text?.toLowerCase().includes(searchText.toLowerCase()) ||
        c.author?.toLowerCase().includes(searchText.toLowerCase())
      )
    : inbox.comments;

  return (
    <div className="animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Engagement Inbox
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Comments and interactions across all clients and platforms
          </p>
        </div>
        <Button onClick={handleSync} disabled={syncing} style={{ background: '#1f2a4d' }}>
          <RefreshCw size={14} className={`mr-2 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing...' : 'Sync Comments'}
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setFlaggedFilter(false); setClientFilter('all'); setPlatformFilter('all'); }}>
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Total Comments</div>
              <div className="text-2xl font-semibold mt-1" style={{ color: '#1f2a4d' }}>{stats.total?.toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFlaggedFilter(true)}>
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Flagged</div>
              <div className="text-2xl font-semibold mt-1 text-red-600">{stats.flagged?.toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Unreplied</div>
              <div className="text-2xl font-semibold mt-1 text-amber-600">{stats.unreplied?.toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Clients</div>
              <div className="text-2xl font-semibold mt-1" style={{ color: '#1f2a4d' }}>{clients.length}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="p-3 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <Filter size={14} className="text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">Filters:</span>
          </div>
          <Select value={clientFilter} onValueChange={v => { setClientFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[180px] h-8 text-xs"><SelectValue placeholder="All Clients" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients</SelectItem>
              {clients.map(c => <SelectItem key={c} value={c}>{c} ({stats?.by_client[c]})</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={platformFilter} onValueChange={v => { setPlatformFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[160px] h-8 text-xs"><SelectValue placeholder="All Platforms" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Platforms</SelectItem>
              {platforms.map(p => <SelectItem key={p} value={p}>{PLATFORM_COLORS[p]?.label || p} ({stats?.by_platform[p]})</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant={flaggedFilter ? 'default' : 'outline'} size="sm" className="h-8 text-xs"
            onClick={() => { setFlaggedFilter(!flaggedFilter); setPage(1); }}
            style={flaggedFilter ? { background: '#e01e25' } : {}}>
            <Flag size={12} className="mr-1" /> Flagged
          </Button>
          <div className="flex-1" />
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2 text-muted-foreground" />
            <Input
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="Search comments..."
              className="h-8 text-xs pl-8 w-48"
            />
          </div>
          {(clientFilter !== 'all' || platformFilter !== 'all' || flaggedFilter || searchText) && (
            <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setClientFilter('all'); setPlatformFilter('all'); setFlaggedFilter(false); setSearchText(''); setPage(1); }}>
              <X size={12} className="mr-1" /> Clear
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Comments List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-16 text-center text-muted-foreground"><Loader2 className="animate-spin mx-auto mb-2" /> Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center">
              <MessageCircle size={32} className="mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">No comments found</p>
              <p className="text-xs text-muted-foreground mt-1">Sprout is cancelled. New comments will resume after Zernio is connected.</p>
            </div>
          ) : (
            <div className="divide-y">
              {filtered.map((comment) => {
                const pmeta = PLATFORM_COLORS[comment.platform] || { bg: '#666', label: comment.network || comment.platform };
                return (
                  <div key={comment.id} className={`px-5 py-4 hover:bg-muted/20 transition-colors ${comment.flagged ? 'bg-red-50/30 border-l-2 border-l-red-400' : ''}`}>
                    <div className="flex items-start gap-3">
                      {/* Platform icon */}
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5"
                        style={{ background: pmeta.bg }}>
                        {(pmeta.label || '?')[0]}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-semibold">{comment.author || 'Unknown'}</span>
                          <Badge variant="outline" className="text-[9px] capitalize">{comment.client_name}</Badge>
                          <Badge variant="secondary" className="text-[9px]">{pmeta.label}</Badge>
                          <span className="text-[10px] text-muted-foreground">{timeAgo(comment.created_time)}</span>
                          {comment.flagged && <Badge className="text-[9px] bg-red-100 text-red-700 border-red-200">Flagged</Badge>}
                          {comment.replied && <Badge className="text-[9px] bg-emerald-100 text-emerald-700 border-emerald-200">Replied</Badge>}
                        </div>
                        <p className="text-sm text-foreground leading-relaxed">{comment.text}</p>
                        {comment.flag_note && (
                          <p className="text-xs text-red-600 mt-1 italic">Note: {comment.flag_note}</p>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {!comment.replied && (
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Mark as replied" onClick={() => handleMarkReplied(comment.id)}>
                            <Check size={13} className="text-emerald-600" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className={`h-7 w-7 ${comment.flagged ? 'text-red-500' : ''}`}
                          title={comment.flagged ? 'Unflag' : 'Flag'}
                          onClick={() => {
                            if (comment.flagged) {
                              handleFlag(comment.id, false);
                            } else {
                              setFlagDialog(comment);
                              setFlagNote('');
                            }
                          }}>
                          <Flag size={13} />
                        </Button>
                        <a href={comment.post_url || 'https://app.sproutsocial.com'} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center justify-center h-7 rounded-md hover:bg-muted transition-colors px-2 gap-1 text-xs text-muted-foreground"
                          title={comment.post_url ? 'View original post' : 'Open in Sprout Social'}>
                          <ExternalLink size={13} />
                          {comment.post_url ? 'View Post' : 'Sprout'}
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {inbox.pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-muted-foreground">
            Showing {((page - 1) * 30) + 1}-{Math.min(page * 30, inbox.total)} of {inbox.total}
          </p>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft size={14} />
            </Button>
            <span className="px-3 py-1 text-sm tabular-nums">{page} / {inbox.pages}</span>
            <Button variant="outline" size="sm" disabled={page >= inbox.pages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      )}

      {/* Flag Dialog */}
      <Dialog open={!!flagDialog} onOpenChange={() => setFlagDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag size={16} className="text-red-500" /> Flag Comment
            </DialogTitle>
          </DialogHeader>
          {flagDialog && (
            <div className="py-3 space-y-3">
              <div className="p-3 bg-muted/30 rounded-lg text-sm">
                <span className="font-semibold">{flagDialog.author}</span>: {flagDialog.text?.substring(0, 120)}
              </div>
              <Input
                value={flagNote}
                onChange={e => setFlagNote(e.target.value)}
                placeholder="Add a note (optional)..."
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFlagDialog(null)}>Cancel</Button>
            <Button onClick={() => handleFlag(flagDialog.id, true)} style={{ background: '#e01e25' }}>
              <Flag size={14} className="mr-2" /> Flag Comment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
